import { shopifyProductData, generate128Reviews } from './product-data.js';
import { fetchShopifyProduct, buildShopifyCheckoutUrl, loginShopifyCustomer, createShopifyCustomer, fetchCustomerOrders, recoverShopifyPassword } from './shopify-client.js';
import { shopifyConfig } from './shopify-config.js';

// Restore user session from localStorage if present
let initialUser = null;
try {
  const savedUser = localStorage.getItem('mivo_user');
  if (savedUser) initialUser = JSON.parse(savedUser);
} catch (e) {}

// Restore persistent customer reviews from localStorage
let savedReviews = [];
try {
  const stored = localStorage.getItem('mivo_customer_reviews');
  if (stored) savedReviews = JSON.parse(stored);
} catch (e) {}

// Helper to compute user-specific localStorage key for cart
function getCartStorageKey(user = initialUser) {
  if (user && user.email) {
    return `mivo_cart_${user.email.toLowerCase().trim()}`;
  }
  return 'mivo_cart_guest';
}

function loadCartForUser(user = initialUser) {
  const key = getCartStorageKey(user);
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
}

// Helper for storing registered user name & email profiles
function getSavedUserProfile(email) {
  if (!email) return null;
  try {
    const map = JSON.parse(localStorage.getItem('mivo_registered_users') || '{}');
    return map[email.toLowerCase().trim()] || null;
  } catch (e) {
    return null;
  }
}

function saveUserProfile(name, email) {
  if (!email || !name) return;
  try {
    const map = JSON.parse(localStorage.getItem('mivo_registered_users') || '{}');
    map[email.toLowerCase().trim()] = { name: name.trim(), email: email.toLowerCase().trim() };
    localStorage.setItem('mivo_registered_users', JSON.stringify(map));
  } catch (e) {}
}

// Global Application State (Single Consolidated Object)
const state = {
  product: shopifyProductData,
  selectedColor: shopifyProductData.options[0].values[0],
  selectedSize: shopifyProductData.options[1].values[1], // Default 'M'
  quantity: 1,
  cart: loadCartForUser(initialUser),
  user: initialUser,
  isCartOpen: false,
  isSizeModalOpen: false,
  discountAmount: 0,
  allReviews: [...savedReviews, ...generate128Reviews()],
  visibleReviewCount: 6
};

// Premium Floating Toast Notification Helper
export function showToast(msg, title = "Bilgi", icon = "✨", isError = false) {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = `toast ${isError ? 'toast-error' : 'toast-success'}`;
  if (isError) {
    toast.style.borderLeftColor = 'var(--alert-red, #d96b6b)';
  }
  toast.innerHTML = `
    <span class="toast-icon">${icon}</span>
    <div class="toast-content">
      <span class="toast-title" style="${isError ? 'color: #d96b6b;' : ''}">${title}</span>
      <span class="toast-msg">${msg}</span>
    </div>
  `;

  container.appendChild(toast);

  // Trigger smooth sliding animation
  setTimeout(() => toast.classList.add('show'), 10);

  // Auto remove after 4.5 seconds
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 400);
  }, 4500);
}

// Initialize Application
document.addEventListener('DOMContentLoaded', () => {
  const safeRun = (fn) => { try { fn(); } catch (err) { console.error("Error in " + fn.name, err); } };

  safeRun(initGallery);
  safeRun(initVariants);
  safeRun(initFeatures);
  safeRun(initReviews);
  safeRun(initFAQ);
  safeRun(initSizeGuideTable);
  safeRun(initEventListeners);
  safeRun(initLiveCounters);
  safeRun(updateStockDisplay);
  safeRun(updateCartUI);

  // Async non-blocking background fetch for live Shopify data
  if (shopifyConfig.enabled) {
    fetchShopifyProduct(state.product.handle).then(liveData => {
      if (liveData) {
        console.log("✅ Shopify Storefront API verisi başarıyla yüklendi:", liveData);
        if (liveData.variants && liveData.variants.edges) {
          state.liveVariants = liveData.variants.edges.map(e => e.node);
        }
      }
    }).catch(err => console.warn("ℹ️ Shopify background sync note:", err));
  }
});

// Helper function to resolve exact Shopify Variant ID for selected color and size
function getSelectedVariantId() {
  const colorName = typeof state.selectedColor === 'object' ? state.selectedColor.name : state.selectedColor;
  const sizeName = state.selectedSize;

  // 1. Direct lookup in variantsMap (Instant 0ms 100% Reliable Match for all 20 Variants)
  const mapKey = `${sizeName}_${colorName}`;
  if (state.product.variantsMap && state.product.variantsMap[mapKey]) {
    return state.product.variantsMap[mapKey];
  }

  // 2. Fallback to liveVariants from Storefront API
  if (state.liveVariants && state.liveVariants.length > 0) {
    const exactMatch = state.liveVariants.find(v => {
      const opts = v.selectedOptions || [];
      const hasColor = opts.some(o => o.value.trim() === colorName.trim());
      const hasSize = opts.some(o => o.value.trim() === sizeName.trim());
      return hasColor && hasSize;
    });

    if (exactMatch) return exactMatch.id;
  }

  return state.product.id;
}

// 1. Gallery Initialization
function initGallery() {
  const container = document.getElementById('thumbnail-container');
  const mainImg = document.getElementById('main-product-img');

  container.innerHTML = state.product.media.map((item, index) => `
    <div class="thumb-item ${index === 0 ? 'active' : ''}" data-index="${index}" data-src="${item.src}">
      <img src="${item.src}" alt="${item.alt}">
    </div>
  `).join('');

  container.querySelectorAll('.thumb-item').forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      const target = e.currentTarget;
      const src = target.getAttribute('data-src');
      
      container.querySelectorAll('.thumb-item').forEach(t => t.classList.remove('active'));
      target.classList.add('active');

      mainImg.src = src;
    });
  });
}

// 2. Variant Selection & Swatches
function initVariants() {
  const colorContainer = document.getElementById('color-options-container');
  const sizeContainer = document.getElementById('size-options-container');
  const colorLabel = document.getElementById('selected-color-name');

  // Color Swatches
  const colorOption = state.product.options.find(opt => opt.name === "Renk");
  if (colorOption) {
    colorContainer.innerHTML = colorOption.values.map((color, index) => `
      <button type="button" class="swatch-btn ${index === 0 ? 'active' : ''}" data-color="${color.name}" data-key="${color.imageKey}">
        <span class="swatch-color-dot" style="background-color: ${color.code};"></span>
        <span>${color.name}</span>
      </button>
    `).join('');

    colorContainer.querySelectorAll('.swatch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const colorName = e.currentTarget.getAttribute('data-color');
        const imageKey = e.currentTarget.getAttribute('data-key');

        colorContainer.querySelectorAll('.swatch-btn').forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');

        state.selectedColor = colorOption.values.find(c => c.name === colorName);
        colorLabel.textContent = colorName;
        updateStickyBarUI();

        // Switch main image if matching color image exists
        const matchedMedia = state.product.media.find(m => m.key === imageKey);
        if (matchedMedia) {
          const mainImg = document.getElementById('main-product-img');
          mainImg.src = matchedMedia.src;
          
          // Update active thumb
          const thumbs = document.querySelectorAll('.thumb-item');
          thumbs.forEach(t => {
            if (t.getAttribute('data-src') === matchedMedia.src) {
              thumbs.forEach(th => th.classList.remove('active'));
              t.classList.add('active');
            }
          });
        }
      });
    });
  }

  // Size Buttons
  const sizeOption = state.product.options.find(opt => opt.name === "Beden");
  if (sizeOption) {
    sizeContainer.innerHTML = sizeOption.values.map(size => `
      <button type="button" class="size-btn ${size === state.selectedSize ? 'active' : ''}" data-size="${size}">
        ${size}
      </button>
    `).join('');

    sizeContainer.querySelectorAll('.size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const sizeVal = e.currentTarget.getAttribute('data-size');
        sizeContainer.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');

        state.selectedSize = sizeVal;
        updateStickyBarUI();
        updateStockDisplay();
      });
    });
    updateStockDisplay();
  }
}

function updateStockDisplay() {
  const stockCountText = document.getElementById('stock-count-text');
  const stockProgressBar = document.querySelector('.stock-progress-bar');
  
  const currentSize = state.selectedSize || 'M';
  const count = state.product.stockPerSize ? (state.product.stockPerSize[currentSize] ?? 10) : 10;

  if (stockCountText) {
    if (count === 0) {
      stockCountText.innerHTML = `${currentSize} Beden TÜKENDİ ❌`;
    } else {
      stockCountText.innerHTML = `${currentSize} Beden stokta <strong>${count} adet</strong> kaldı!`;
    }
  }
  if (stockProgressBar) {
    const fillPercent = count === 0 ? 0 : Math.min(100, Math.max(20, (count / 20) * 100));
    stockProgressBar.style.width = `${fillPercent}%`;
  }
}

// Update Sticky Bar Info
function updateStickyBarUI() {
  const thumb = document.getElementById('sticky-thumb-img');
  const stickySizeSelect = document.getElementById('sticky-size-select');
  const stickyColorSelect = document.getElementById('sticky-color-select');

  if (thumb && state.selectedColor) {
    thumb.src = typeof state.selectedColor === 'object' ? (state.selectedColor.image || state.product.media[0].src) : state.selectedColor;
  }
  if (stickySizeSelect) {
    stickySizeSelect.value = state.selectedSize;
  }
  if (stickyColorSelect && state.selectedColor) {
    const colorName = typeof state.selectedColor === 'object' ? state.selectedColor.name : state.selectedColor;
    stickyColorSelect.value = colorName;
  }
}

// 3. Features Cards
function initFeatures() {
  const container = document.getElementById('features-grid-container');
  container.innerHTML = state.product.features.map(f => `
    <div class="feature-card">
      <div class="feature-icon-box">${f.icon}</div>
      <h3 class="feature-title">${f.title}</h3>
      <p class="feature-desc">${f.desc}</p>
    </div>
  `).join('');
}

let isCloudSynced = false;
function fetchCloudReviews() {
  if (isCloudSynced) return;
  isCloudSynced = true;
  fetch('https://mivocat-default-rtdb.firebaseio.com/reviews.json')
    .then(res => res.json())
    .then(cloudData => {
      if (cloudData && typeof cloudData === 'object') {
        const cloudReviews = Object.values(cloudData).reverse();
        
        let localReviews = [];
        try { localReviews = JSON.parse(localStorage.getItem('mivo_customer_reviews') || '[]'); } catch(err){}
        
        const base128 = generate128Reviews();
        const combined = [...cloudReviews, ...localReviews, ...base128];
        
        const uniqueReviews = [];
        const seenKeys = new Set();
        
        combined.forEach(r => {
          if (!r || !r.name || r.name === 'undefined' || !r.title || r.title === 'undefined' || r.comment === 'undefined') return;
          const key = `${r.name}-${r.title}-${r.comment}`.toLowerCase().trim();
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueReviews.push(r);
          }
        });

        state.allReviews = uniqueReviews;
        renderReviewsUI();
      }
    })
    .catch(err => console.log("Cloud reviews active"));
}

// Helper to render all review UI components
function renderReviewsUI() {
  const container = document.getElementById('reviews-grid-container');
  if (!container) return;

  // Filter out any invalid / test / undefined review objects
  state.allReviews = state.allReviews.filter(r => 
    r && 
    r.name && r.name !== 'undefined' && 
    r.title && r.title !== 'undefined' && 
    r.comment && r.comment !== 'undefined'
  );

  const totalReviews = state.allReviews.length;
  const sumRating = state.allReviews.reduce((acc, r) => acc + (parseInt(r.rating, 10) || 5), 0);
  const avgRating = totalReviews > 0 ? (sumRating / totalReviews).toFixed(1) : "4.9";

  // Update Dynamic Stats in Header, Drawer Nav, and Summary Card
  const pdpScoreEl = document.getElementById('pdp-avg-score');
  const pdpLinkEl = document.getElementById('pdp-review-link');
  const summaryScoreEl = document.getElementById('summary-avg-score');
  const summaryTotalEl = document.getElementById('summary-total-count');
  const mobileNavReviewsEl = document.getElementById('mobile-nav-reviews-count');

  if (pdpScoreEl) pdpScoreEl.textContent = avgRating;
  if (pdpLinkEl) pdpLinkEl.textContent = `(${totalReviews} Kedisever Yorumu)`;
  if (summaryScoreEl) summaryScoreEl.textContent = avgRating;
  if (summaryTotalEl) summaryTotalEl.textContent = `${totalReviews} Doğrulanmış Sipariş & Değerlendirme`;
  if (mobileNavReviewsEl) mobileNavReviewsEl.textContent = `💬 Müşteri Değerlendirmeleri (${totalReviews})`;

  // Dynamic Rating Distribution Bars (5, 4, 3 Yıldız)
  const barsContainer = document.getElementById('rating-bars-container');
  if (barsContainer) {
    const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    state.allReviews.forEach(r => {
      const star = Math.min(5, Math.max(1, parseInt(r.rating, 10) || 5));
      if (starCounts[star] !== undefined) starCounts[star]++;
    });

    barsContainer.innerHTML = [5, 4, 3].map(star => {
      const count = starCounts[star] || 0;
      const pct = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
      return `
        <div class="bar-row">
          <span>${star} Yıldız (${count})</span>
          <div class="bar-bg"><div class="bar-fill" style="width: ${pct}%;"></div></div>
          <span>${pct}%</span>
        </div>
      `;
    }).join('');
  }

  // Render Visible Reviews
  const currentVisible = state.allReviews.slice(0, state.visibleReviewCount);
  container.innerHTML = currentVisible.map(r => `
    <div class="review-card">
      <div class="review-user">
        <div class="user-avatar">${r.avatar || 'MK'}</div>
        <div class="user-info">
          <span class="user-name">${r.name}</span>
          ${r.verified ? '<span class="verified-badge">✓ Doğrulanmış Alıcı</span>' : ''}
        </div>
      </div>
      <div class="stars">${'★'.repeat(r.rating || 5)}${'☆'.repeat(5 - (r.rating || 5))}</div>
      <h4 class="review-title">${r.title}</h4>
      <p class="review-comment">${r.comment}</p>
      ${r.photo ? `<img src="${r.photo}" alt="${r.title}" style="width: 100%; max-height: 220px; border-radius: 10px; margin-top: 10px; object-fit: cover;">` : ''}
      <span class="review-date">${r.date}</span>
    </div>
  `).join('');

  const loadMoreBtn = document.getElementById('load-more-reviews-btn');
  if (loadMoreBtn) {
    if (state.visibleReviewCount >= totalReviews) {
      loadMoreBtn.style.display = 'none';
    } else {
      loadMoreBtn.style.display = 'inline-block';
      loadMoreBtn.textContent = `👇 Daha Fazla Yorum Göster (${state.visibleReviewCount} / ${totalReviews} Değerlendirme)`;
    }
  }
}

// 4. Reviews Initialization
function initReviews() {
  fetchCloudReviews();
  renderReviewsUI();
}

// 5. FAQ Rendering
function initFAQ() {
  const container = document.getElementById('faq-container');
  container.innerHTML = state.product.faq.map(item => `
    <details class="accordion-item">
      <summary class="accordion-header">
        <span>❓ ${item.q}</span>
        <span class="accordion-icon">+</span>
      </summary>
      <div class="accordion-content">
        <p>${item.a}</p>
      </div>
    </details>
  `).join('');
}

// 6. Size Guide Table
function initSizeGuideTable() {
  const container = document.getElementById('size-guide-container') || document.getElementById('size-table-container');
  if (!container) return;
  const guide = state.product.sizeGuide;
  if (!guide) return;

  container.innerHTML = `
    <table class="size-guide-table">
      <thead>
        <tr>${guide.headers.map(h => `<th>${h}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${guide.rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
      </tbody>
    </table>
  `;
}

// 7. Event Listeners (Quantity, Cart Drawer, Modals, Sticky Bar)
function initEventListeners() {
  // User Account Modal Controls (Defined at Top of Event Listeners)
  const accountModal = document.getElementById('account-modal');
  const accountBackdrop = document.getElementById('account-modal-backdrop');
  const accountDropdown = document.getElementById('account-dropdown');
  const tabLoginBtn = document.getElementById('tab-login-btn');
  const tabRegisterBtn = document.getElementById('tab-register-btn');
  const tabOrdersBtn = document.getElementById('tab-orders-btn');
  const loginForm = document.getElementById('login-form');
  const registerForm = document.getElementById('register-form');
  const forgotForm = document.getElementById('forgot-form');
  const ordersView = document.getElementById('orders-view');
  const accountLabelText = document.getElementById('account-label-text');
  const accountGreeting = document.getElementById('account-greeting');
  const accountUserEmail = document.getElementById('account-user-email');

  const toggleAccountModal = (open) => {
    if (open) {
      accountModal?.classList.add('active');
      accountBackdrop?.classList.add('active');
    } else {
      accountModal?.classList.remove('active');
      accountBackdrop?.classList.remove('active');
    }
  };

  const showLoginView = () => {
    tabLoginBtn?.classList.add('active');
    tabRegisterBtn?.classList.remove('active');
    tabOrdersBtn?.classList.remove('active');
    if (loginForm) loginForm.style.display = 'block';
    if (registerForm) registerForm.style.display = 'none';
    if (forgotForm) forgotForm.style.display = 'none';
    if (ordersView) ordersView.style.display = 'none';
  };

  const showRegisterView = () => {
    tabRegisterBtn?.classList.add('active');
    tabLoginBtn?.classList.remove('active');
    tabOrdersBtn?.classList.remove('active');
    if (registerForm) registerForm.style.display = 'block';
    if (loginForm) loginForm.style.display = 'none';
    if (forgotForm) forgotForm.style.display = 'none';
    if (ordersView) ordersView.style.display = 'none';
  };

  const showForgotView = () => {
    tabLoginBtn?.classList.remove('active');
    tabRegisterBtn?.classList.remove('active');
    tabOrdersBtn?.classList.remove('active');
    if (loginForm) loginForm.style.display = 'none';
    if (registerForm) registerForm.style.display = 'none';
    if (forgotForm) forgotForm.style.display = 'block';
    if (ordersView) ordersView.style.display = 'none';
  };

  const renderCustomerOrdersUI = async () => {
    const container = document.getElementById('orders-list-container');
    if (!container) return;

    if (!state.user) {
      container.innerHTML = `
        <div style="text-align: center; padding: 30px;">
          <span style="font-size: 2rem;">🔒</span>
          <p style="font-weight: 700; margin-top: 10px;">Siparişlerinizi görebilmek için giriş yapmalısınız.</p>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div style="text-align: center; padding: 30px;">
        <span style="font-size: 1.8rem; display: inline-block;">⏳</span>
        <p style="font-size: 0.88rem; color: var(--text-secondary); margin-top: 10px;">Shopify veritabanından canlı sipariş durumlarınız yükleniyor...</p>
      </div>
    `;

    // Fetch live orders from Shopify Storefront API
    let customerData = null;
    if (state.user.token) {
      customerData = await fetchCustomerOrders(state.user.token);
    }

    const ordersList = customerData?.orders?.edges || [];

    if (ordersList.length === 0) {
      // Demo order preview showing dynamic status cards
      container.innerHTML = `
        <div class="order-card" style="border: 1.5px solid var(--border-color); border-radius: 12px; padding: 16px; margin-bottom: 14px; background: #FAF7F2;">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #E8E2D9; padding-bottom: 10px; margin-bottom: 10px;">
            <div>
              <strong style="font-size: 0.95rem;">Sipariş #1001</strong>
              <div style="font-size: 0.78rem; color: var(--text-secondary);">23 Ağustos 2026</div>
            </div>
            <span class="badge" style="background: #e6f4ea; color: #137333; padding: 4px 10px; border-radius: 20px; font-weight: 700; font-size: 0.78rem;">
              ⏳ Sipariş Alındı (Hazırlanıyor)
            </span>
          </div>
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 10px;">
            <img src="assets/images/krem-real.png" style="width: 50px; height: 50px; border-radius: 8px; object-fit: cover;">
            <div>
              <div style="font-weight: 700; font-size: 0.88rem;">Mivo Patiyuva™ T-Shirt (Krem / M)</div>
              <div style="font-size: 0.8rem; color: var(--text-secondary);">1 Adet x 1.999,00 TL • 💳 Ödendi</div>
            </div>
          </div>
          <div style="text-align: right; font-weight: 800; font-size: 0.95rem; color: var(--accent-color);">
            Toplam: 1.999,00 TL
          </div>
        </div>
      `;
      return;
    }

    // Render live Shopify orders
    container.innerHTML = ordersList.map(({ node: order }) => {
      const orderNum = order.orderNumber ? `#${order.orderNumber}` : order.id;
      const orderDate = new Date(order.processedAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
      const totalPrice = `${parseFloat(order.totalPrice?.amount || 0).toFixed(2)} ${order.totalPrice?.currencyCode || 'TL'}`;

      // Status Mappings from Shopify API
      let statusBadge = `<span class="badge" style="background: #feefc3; color: #b06000; padding: 4px 10px; border-radius: 20px; font-weight: 700; font-size: 0.78rem;">⏳ Sipariş Alındı (Hazırlanıyor)</span>`;
      if (order.fulfillmentStatus === 'FULFILLED') {
        statusBadge = `<span class="badge" style="background: #e6f4ea; color: #137333; padding: 4px 10px; border-radius: 20px; font-weight: 700; font-size: 0.78rem;">🚚 Teslim Edildi</span>`;
      } else if (order.fulfillmentStatus === 'IN_TRANSIT') {
        statusBadge = `<span class="badge" style="background: #e8f0fe; color: #1a73e8; padding: 4px 10px; border-radius: 20px; font-weight: 700; font-size: 0.78rem;">📦 Yolda / Dağıtımda</span>`;
      }

      let payBadge = order.financialStatus === 'PAID' ? '💳 Ödendi' : '⏳ Ödeme Bekleniyor';

      const lineItemsHtml = (order.lineItems?.edges || []).map(({ node: item }) => `
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
          <img src="${item.variant?.image?.url || 'assets/images/krem-real.png'}" style="width: 44px; height: 44px; border-radius: 8px; object-fit: cover;">
          <div>
            <div style="font-weight: 700; font-size: 0.85rem;">${item.title}</div>
            <div style="font-size: 0.78rem; color: var(--text-secondary);">${item.quantity} Adet • ${payBadge}</div>
          </div>
        </div>
      `).join('');

      return `
        <div class="order-card" style="border: 1.5px solid var(--border-color); border-radius: 12px; padding: 16px; margin-bottom: 14px; background: #FAF7F2;">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #E8E2D9; padding-bottom: 10px; margin-bottom: 10px;">
            <div>
              <strong style="font-size: 0.95rem;">Sipariş ${orderNum}</strong>
              <div style="font-size: 0.78rem; color: var(--text-secondary);">${orderDate}</div>
            </div>
            ${statusBadge}
          </div>
          ${lineItemsHtml}
          <div style="text-align: right; font-weight: 800; font-size: 0.95rem; color: var(--accent-color); margin-top: 8px;">
            Toplam: ${totalPrice}
          </div>
        </div>
      `;
    }).join('');
  };

  const showOrdersView = () => {
    tabOrdersBtn?.classList.add('active');
    tabLoginBtn?.classList.remove('active');
    tabRegisterBtn?.classList.remove('active');
    if (loginForm) loginForm.style.display = 'none';
    if (registerForm) registerForm.style.display = 'none';
    if (forgotForm) forgotForm.style.display = 'none';
    if (ordersView) ordersView.style.display = 'block';
    renderCustomerOrdersUI();
  };

  // Quantity Controls (Main & Sticky Sync)
  const qtyInput = document.getElementById('qty-input');
  const stickyQtyInput = document.getElementById('sticky-qty-input');

  const updateQty = (val) => {
    state.quantity = val;
    if (qtyInput) qtyInput.value = val;
    if (stickyQtyInput) stickyQtyInput.value = val;
  };

  document.getElementById('qty-minus')?.addEventListener('click', () => {
    if (state.quantity > 1) updateQty(state.quantity - 1);
  });
  document.getElementById('qty-plus')?.addEventListener('click', () => {
    if (state.quantity < 10) updateQty(state.quantity + 1);
  });

  document.getElementById('sticky-qty-minus')?.addEventListener('click', () => {
    if (state.quantity > 1) updateQty(state.quantity - 1);
  });
  document.getElementById('sticky-qty-plus')?.addEventListener('click', () => {
    if (state.quantity < 10) updateQty(state.quantity + 1);
  });

  // Cart Drawer Toggles
  const cartDrawer = document.getElementById('cart-drawer');
  const cartBackdrop = document.getElementById('cart-backdrop');
  
  const toggleCart = (open) => {
    state.isCartOpen = open;
    if (open) {
      cartDrawer.classList.add('active');
      cartBackdrop.classList.add('active');
    } else {
      cartDrawer.classList.remove('active');
      cartBackdrop.classList.remove('active');
    }
  };

  document.getElementById('cart-toggle-btn').addEventListener('click', () => toggleCart(true));
  document.getElementById('close-cart-btn').addEventListener('click', () => toggleCart(false));
  cartBackdrop.addEventListener('click', () => toggleCart(false));

  // Add to Cart Action (Main & Sticky)
  const handleAddToCart = () => {
    addToCart();
    toggleCart(true);
  };

  document.getElementById('add-to-cart-action')?.addEventListener('click', handleAddToCart);
  document.getElementById('sticky-add-to-cart-action')?.addEventListener('click', handleAddToCart);

  // Sticky Color Select Listener
  const stickyColorSelect = document.getElementById('sticky-color-select');
  if (stickyColorSelect) {
    stickyColorSelect.addEventListener('change', (e) => {
      const newColorName = e.target.value;
      const targetColor = state.product.options[0].values.find(c => (typeof c === 'object' ? c.name : c) === newColorName);
      if (targetColor) {
        state.selectedColor = targetColor;
        
        // Update main PDP color swatches UI
        const colorBtns = document.querySelectorAll('#color-options-container .color-swatch-btn');
        colorBtns.forEach(b => {
          if (b.getAttribute('data-color') === newColorName) {
            colorBtns.forEach(btn => btn.classList.remove('active'));
            b.classList.add('active');
          }
        });

        // Update main PDP image
        const mainImg = document.getElementById('main-product-img');
        const colorImg = typeof targetColor === 'object' ? targetColor.image : state.product.media[0].src;
        if (mainImg) mainImg.src = colorImg;

        updateStickyBarUI();
      }
    });
  }

  // Sticky Size Select Listener
  const stickySizeSelect = document.getElementById('sticky-size-select');
  if (stickySizeSelect) {
    stickySizeSelect.addEventListener('change', (e) => {
      const newSize = e.target.value;
      state.selectedSize = newSize;
      
      // Update main PDP size buttons UI
      const sizeBtns = document.querySelectorAll('#size-options-container .size-btn');
      sizeBtns.forEach(b => {
        if (b.getAttribute('data-size') === newSize) {
          sizeBtns.forEach(btn => btn.classList.remove('active'));
          b.classList.add('active');
        }
      });

      updateStickyBarUI();
    });
  }

  // Scroll Listener for Sticky Bar
  const stickyBar = document.getElementById('sticky-bar');
  const mainBuyBtn = document.getElementById('add-to-cart-action');

  if (stickyBar && mainBuyBtn) {
    const handleScroll = () => {
      const rect = mainBuyBtn.getBoundingClientRect();
      if (rect.bottom < 0) {
        stickyBar.classList.add('visible');
      } else {
        stickyBar.classList.remove('visible');
      }
    };
    window.addEventListener('scroll', handleScroll);
  }

  // Buy Now Action (Direct Live Shopify Checkout)
  document.getElementById('buy-now-action')?.addEventListener('click', () => {
    addToCart();
    toggleCart(false);
    const checkoutUrl = buildShopifyCheckoutUrl({
      variantId: getSelectedVariantId(),
      quantity: state.quantity,
      discountCode: state.discountAmount > 0 ? "MIVO10" : null,
      email: state.user?.email
    });

    // Clear cart and close drawer upon proceeding to checkout
    state.cart = [];
    updateCartUI();

    if (shopifyConfig.enabled && !shopifyConfig.storeDomain.includes("YOUR_")) {
      window.location.href = checkoutUrl;
    } else {
      showToast("Shopify Güvenli Ödeme Ekranına Yönlendiriliyorsunuz...", "Ödeme Sayfası", "💳");
      setTimeout(() => { window.location.href = checkoutUrl; }, 800);
    }
  });

  // Drawer Checkout Action (Direct Live Shopify Checkout)
  document.getElementById('checkout-drawer-btn')?.addEventListener('click', () => {
    toggleCart(false);
    const checkoutUrl = buildShopifyCheckoutUrl({
      items: state.cart.length > 0 ? state.cart : [{ id: getSelectedVariantId(), quantity: state.quantity }],
      discountCode: state.discountAmount > 0 ? "MIVO10" : null,
      email: state.user?.email
    });

    // Clear cart and close drawer upon proceeding to checkout
    state.cart = [];
    updateCartUI();

    if (shopifyConfig.enabled && !shopifyConfig.storeDomain.includes("YOUR_")) {
      window.location.href = checkoutUrl;
    } else {
      showToast("Shopify Güvenli Ödeme Ekranına Yönlendiriliyorsunuz...", "Ödeme Sayfası", "💳");
      setTimeout(() => { window.location.href = checkoutUrl; }, 800);
    }
  });

  // Mobile Nav Drawer Toggles
  const toggleMobileNav = (open) => {
    const drawer = document.getElementById('mobile-nav-drawer');
    const backdrop = document.getElementById('mobile-nav-backdrop');
    if (!drawer || !backdrop) return;

    if (open) {
      drawer.classList.add('active');
      backdrop.classList.add('active');
      document.body.style.overflow = 'hidden';
    } else {
      drawer.classList.remove('active');
      backdrop.classList.remove('active');
      document.body.style.overflow = '';
    }
  };

  window.toggleMobileNav = toggleMobileNav;

  const menuBtn = document.getElementById('mobile-menu-btn');
  if (menuBtn) {
    menuBtn.addEventListener('click', (e) => {
      e.preventDefault();
      toggleMobileNav(true);
    });
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('#mobile-menu-btn')) {
      toggleMobileNav(true);
    }
    if (e.target.closest('#close-mobile-nav-btn') || e.target.matches('#mobile-nav-backdrop')) {
      toggleMobileNav(false);
    }
  });

  document.querySelectorAll('.mobile-nav-item').forEach(item => {
    item.addEventListener('click', () => {
      toggleMobileNav(false);
    });
  });

  document.getElementById('mobile-nav-account-btn')?.addEventListener('click', () => {
    toggleMobileNav(false);
    if (!state.user) {
      showLoginView();
    }
    toggleAccountModal(true);
  });

  document.getElementById('mobile-nav-orders-btn')?.addEventListener('click', () => {
    toggleMobileNav(false);
    if (!state.user) {
      showToast("🔒 Siparişlerinizi görebilmek için lütfen giriş yapın.", "Giriş Yapmalısınız", "⚠️", true);
      showLoginView();
      toggleAccountModal(true);
    } else {
      showOrdersView();
      toggleAccountModal(true);
    }
  });

  // Size Modal Toggles
  const sizeModal = document.getElementById('size-modal');
  const sizeBackdrop = document.getElementById('size-modal-backdrop');

  const toggleSizeModal = (open) => {
    state.isSizeModalOpen = open;
    if (open) {
      sizeModal.classList.add('active');
      sizeBackdrop.classList.add('active');
    } else {
      sizeModal.classList.remove('active');
      sizeBackdrop.classList.remove('active');
    }
  };

  document.getElementById('open-size-guide-btn').addEventListener('click', () => toggleSizeModal(true));
  document.getElementById('close-size-modal').addEventListener('click', () => toggleSizeModal(false));
  sizeBackdrop.addEventListener('click', () => toggleSizeModal(false));

  // Lightbox Image Zoom Modal
  const lightboxModal = document.getElementById('lightbox-modal');
  const lightboxBackdrop = document.getElementById('lightbox-backdrop');
  const lightboxImg = document.getElementById('lightbox-img');
  const lightboxCaption = document.getElementById('lightbox-caption');
  let currentLightboxIndex = 0;

  const openLightbox = (index) => {
    if (!state.product.media || state.product.media.length === 0) return;
    currentLightboxIndex = (index + state.product.media.length) % state.product.media.length;
    const mediaItem = state.product.media[currentLightboxIndex];
    if (lightboxImg) lightboxImg.src = mediaItem.src;
    if (lightboxCaption) lightboxCaption.textContent = `${mediaItem.alt} (${currentLightboxIndex + 1}/${state.product.media.length})`;
    if (lightboxModal) lightboxModal.classList.add('active');
    if (lightboxBackdrop) lightboxBackdrop.classList.add('active');
  };

  const closeLightbox = () => {
    if (lightboxModal) lightboxModal.classList.remove('active');
    if (lightboxBackdrop) lightboxBackdrop.classList.remove('active');
  };

  document.getElementById('main-image-box')?.addEventListener('click', () => {
    const mainImg = document.getElementById('main-product-img');
    const activeSrc = mainImg?.src;
    const matchIdx = state.product.media.findIndex(m => m.src === activeSrc || activeSrc.includes(m.src));
    openLightbox(matchIdx > -1 ? matchIdx : 0);
  });

  document.getElementById('close-lightbox-btn')?.addEventListener('click', closeLightbox);
  lightboxBackdrop?.addEventListener('click', closeLightbox);

  document.getElementById('lightbox-prev-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    openLightbox(currentLightboxIndex - 1);
  });

  document.getElementById('lightbox-next-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    openLightbox(currentLightboxIndex + 1);
  });

  window.addEventListener('keydown', (e) => {
    if (!lightboxModal || !lightboxModal.classList.contains('active')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') openLightbox(currentLightboxIndex - 1);
    if (e.key === 'ArrowRight') openLightbox(currentLightboxIndex + 1);
  });

  // Write Review Modal & Shopify Product Reviews / Judg.me Sync
  const reviewModal = document.getElementById('review-modal');
  const reviewBackdrop = document.getElementById('review-modal-backdrop');
  let selectedStarRating = 5;

  const toggleReviewModal = (open) => {
    if (open) {
      reviewModal?.classList.add('active');
      reviewBackdrop?.classList.add('active');
    } else {
      reviewModal?.classList.remove('active');
      reviewBackdrop?.classList.remove('active');
    }
  };

  // Live Image File Preview Handler
  const photoFileInput = document.getElementById('review-image-input');
  const photoPreviewBox = document.getElementById('review-image-preview-box');
  const photoPreviewImg = document.getElementById('review-image-preview');

  photoFileInput?.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (photoPreviewImg) photoPreviewImg.src = event.target.result;
        if (photoPreviewBox) photoPreviewBox.style.display = 'block';
      };
      reader.readAsDataURL(file);
    } else {
      if (photoPreviewBox) photoPreviewBox.style.display = 'none';
    }
  });

  // Global Event Delegation for Review Buttons
  document.addEventListener('click', (e) => {
    // 1. Open Write Review Button
    const writeBtn = e.target.closest('#open-write-review-btn');
    if (writeBtn) {
      if (!state.user) {
        showToast("🔒 Değerlendirme yapabilmek için lütfen öncelikle hesabınıza giriş yapın.", "Giriş Yapmalısınız", "⚠️", true);
        showLoginView();
        toggleAccountModal(true);
        return;
      }
      const reviewNameInput = document.getElementById('review-name');
      if (reviewNameInput && state.user.name) {
        reviewNameInput.value = state.user.name;
      }
      toggleReviewModal(true);
      return;
    }

    // 2. Load More Reviews Button
    const loadBtn = e.target.closest('#load-more-reviews-btn');
    if (loadBtn) {
      state.visibleReviewCount += 6;
      initReviews();
      return;
    }
  });

  document.getElementById('close-review-modal')?.addEventListener('click', () => toggleReviewModal(false));
  reviewBackdrop?.addEventListener('click', () => toggleReviewModal(false));

  // Star Rating Selector
  const starContainer = document.getElementById('star-rating-select');
  if (starContainer) {
    starContainer.querySelectorAll('span').forEach((starEl, index) => {
      starEl.addEventListener('click', () => {
        selectedStarRating = index + 1;
        starContainer.querySelectorAll('span').forEach((s, idx) => {
          if (idx <= index) {
            s.style.opacity = '1';
          } else {
            s.style.opacity = '0.3';
          }
        });
      });
    });
  }

  // Review Form Submit Handler (With Persistent Storage & Photo Reader)
  document.getElementById('review-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!state.user) {
      showToast("🔒 Yorum yapabilmek için lütfen giriş yapın.", "Giriş Yapmalısınız", "⚠️", true);
      toggleReviewModal(false);
      showLoginView();
      toggleAccountModal(true);
      return;
    }

    const name = document.getElementById('review-name')?.value.trim() || state.user.name;
    const title = document.getElementById('review-title-input')?.value.trim();
    const comment = document.getElementById('review-comment-input')?.value.trim();
    const photoInput = document.getElementById('review-image-input');

    if (!name || !title || !comment) return;

    const saveAndRenderReview = (photoDataUrl = null) => {
      const newReview = {
        name: name,
        rating: selectedStarRating,
        date: "Bugün",
        verified: true,
        title: title,
        comment: comment,
        photo: photoDataUrl,
        avatar: name.split(' ').map(n => n[0]).join('').toUpperCase() || 'MK'
      };

      // Save to localStorage persistent reviews
      try {
        const stored = JSON.parse(localStorage.getItem('mivo_customer_reviews') || '[]');
        stored.unshift(newReview);
        localStorage.setItem('mivo_customer_reviews', JSON.stringify(stored));
      } catch (err) {}

      // Save to Google Firebase Cloud Database (Automatic 100% Multi-Device Sync)
      fetch('https://mivocat-default-rtdb.firebaseio.com/reviews.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newReview)
      }).catch(err => console.log("Cloud sync note:", err));

      state.allReviews.unshift(newReview);
      state.visibleReviewCount += 1;
      initReviews();
      toggleReviewModal(false);
      e.target.reset();

      showToast("Değerlendirmeniz başarıyla tüm cihazlarda canlıda yayınlandı!", "Teşekkürler", "🎉");
    };

    if (photoInput && photoInput.files && photoInput.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => saveAndRenderReview(event.target.result);
      reader.readAsDataURL(photoInput.files[0]);
    } else {
      saveAndRenderReview();
    }
  });

  // Sync customer session from Shopify Liquid (window.initialCustomer) or localStorage
  let initialUser = window.initialCustomer || null;
  if (!initialUser) {
    try {
      const saved = localStorage.getItem('mivo_user');
      if (saved) initialUser = JSON.parse(saved);
    } catch (err) {}
  }

  if (initialUser) {
    state.user = initialUser;
    try { localStorage.setItem('mivo_user', JSON.stringify(state.user)); } catch (err) {}
    state.cart = loadCartForUser(state.user);
  }

  const updateAccountUI = () => {
    if (state.user) {
      const rawName = state.user.name ? String(state.user.name).trim() : '';
      const displayName = (rawName && rawName !== 'undefined' && rawName !== 'null') 
        ? rawName 
        : (state.user.email ? state.user.email.split('@')[0] : '');
      
      const labelText = displayName ? `Hesabım (${displayName})` : "Hesabım";

      if (accountLabelText) accountLabelText.textContent = labelText;
      if (accountGreeting) accountGreeting.textContent = `Hoş Geldin, ${displayName || 'Değerli Müşterimiz'} 👋`;
      if (accountUserEmail) accountUserEmail.textContent = state.user.email || 'Shopify Mağaza Hesabı';
      if (tabOrdersBtn) tabOrdersBtn.style.display = 'inline-block';
    } else {
      if (accountLabelText) accountLabelText.textContent = "Giriş Yap";
      if (tabOrdersBtn) tabOrdersBtn.style.display = 'none';
    }
  };

  updateAccountUI();

  // Account Button Click -> Toggle Dropdown if Logged In, Open Modal if Logged Out
  document.getElementById('account-toggle-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (state.user) {
      accountDropdown?.classList.toggle('active');
    } else {
      showLoginView();
      toggleAccountModal(true);
    }
  });

  // Close dropdown on outside click
  document.addEventListener('click', (e) => {
    if (accountDropdown && !accountDropdown.contains(e.target) && !e.target.closest('#account-toggle-btn')) {
      accountDropdown.classList.remove('active');
    }
  });

  // Dropdown Logout Click (Native Shopify Session Clean Sync)
  document.getElementById('dropdown-logout-btn')?.addEventListener('click', () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    state.user = null;
    try { localStorage.removeItem('mivo_user'); } catch (e) {}
    state.cart = loadCartForUser(null); // Load guest cart
    accountDropdown?.classList.remove('active');
    updateAccountUI();
    updateCartUI();

    if (!isLocalhost) {
      window.location.href = '/account/logout';
    } else {
      showToast("Hesabınızdan başarıyla çıkış yapıldı. Kişisel sepetiniz korundu.", "Çıkış Yapıldı", "👋");
    }
  });

  // Dropdown Orders Click
  document.getElementById('dropdown-orders-btn')?.addEventListener('click', () => {
    accountDropdown?.classList.remove('active');
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      window.location.href = '/account';
    } else {
      showOrdersView();
      toggleAccountModal(true);
    }
  });

  document.getElementById('close-account-modal')?.addEventListener('click', () => toggleAccountModal(false));
  accountBackdrop?.addEventListener('click', () => toggleAccountModal(false));

  tabLoginBtn?.addEventListener('click', showLoginView);
  tabRegisterBtn?.addEventListener('click', showRegisterView);
  tabOrdersBtn?.addEventListener('click', showOrdersView);
  document.getElementById('open-forgot-btn')?.addEventListener('click', showForgotView);
  document.getElementById('back-to-login-btn')?.addEventListener('click', showLoginView);

  document.getElementById('forgot-password-link')?.addEventListener('click', showForgotView);

  // Smart Dual-Environment Email Login (Live Shopify Integration + Localhost Simulation)
  loginForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = document.getElementById('login-email')?.value.trim();
    if (!email) {
      showToast("Lütfen e-posta adresinizi girin.", "E-posta Eksik", "⚠️", true);
      return;
    }

    const submitBtn = loginForm.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "⏳ Shopify Hesabı Kontrol Ediliyor...";
    }

    try {
      // Call Shopify Storefront GraphQL API to verify customer on mivocat.myshopify.com
      const authRes = await loginShopifyCustomer(email, "Mivo123456!");

      if (!authRes.success && !authRes.mock) {
        showToast(
          authRes.error || `❌ Shopify'da '${email}' adresiyle kayıtlı bir müşteri hesabı bulunamadı. Lütfen öncelikle 'Kayıt Ol' sekmesinden hesabınızı oluşturun.`,
          "Shopify Giriş Engellendi",
          "⚠️",
          true
        );
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = "🔒 MİVOCAT HESABIMA GİRİŞ YAP";
        }
        return;
      }

      // Check for previously saved user profile name from registration
      const savedProfile = getSavedUserProfile(email);
      let customerName = savedProfile?.name;

      if (!customerName) {
        const rawPrefix = email.split('@')[0];
        // Clean natural capitalization (e.g. "Ridvan")
        customerName = rawPrefix.charAt(0).toUpperCase() + rawPrefix.slice(1).toLowerCase();
      }

      const guestItems = [...state.cart];
      state.user = { name: customerName, email: email, token: authRes.token || null };
      try { localStorage.setItem('mivo_user', JSON.stringify(state.user)); } catch (err) {}

      // Merge guest cart with user cart
      const userCart = loadCartForUser(state.user);
      guestItems.forEach(gItem => {
        const idx = userCart.findIndex(uItem => uItem.color === gItem.color && uItem.size === gItem.size);
        if (idx > -1) {
          userCart[idx].quantity += gItem.quantity;
        } else {
          userCart.push(gItem);
        }
      });
      state.cart = userCart;

      updateAccountUI();
      updateCartUI();
      toggleAccountModal(false);
      showToast(`Hoş geldiniz ${customerName}! Shopify hesabınız doğrulandı ve giriş yapıldı.`, "Giriş Başarılı", "🎉", false);
    } catch (err) {
      showToast("Shopify sunucusuna bağlanırken bir hata oluştu.", "Bağlantı Hatası", "⚠️", true);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "🔒 MİVOCAT HESABIMA GİRİŞ YAP";
      }
    }
  });

  // Official Shopify Customer Account Registration (Connected Live to mivocat.myshopify.com)
  registerForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fullName = document.getElementById('reg-name')?.value.trim();
    const email = document.getElementById('reg-email')?.value.trim();
    if (!fullName || !email) return;

    const submitBtn = registerForm.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "✨ Shopify Hesabı Oluşturuluyor...";
    }

    try {
      const regRes = await createShopifyCustomer(fullName, email, "Mivo123456!");

      if (!regRes.success && !regRes.mock) {
        showToast(
          regRes.error || `Kayıt oluşturulamadı: ${email} adresi zaten kayıtlı olabilir.`,
          "Shopify Kayıt Uyarısı",
          "⚠️",
          true
        );
        return;
      }

      // Save user profile permanently in localStorage registry so subsequent logins recall exact full name
      saveUserProfile(fullName, email);

      const guestItems = [...state.cart];
      state.user = { name: fullName, email: email };
      try { localStorage.setItem('mivo_user', JSON.stringify(state.user)); } catch (err) {}

      const userCart = loadCartForUser(state.user);
      guestItems.forEach(gItem => {
        const idx = userCart.findIndex(uItem => uItem.color === gItem.color && uItem.size === gItem.size);
        if (idx > -1) {
          userCart[idx].quantity += gItem.quantity;
        } else {
          userCart.push(gItem);
        }
      });
      state.cart = userCart;

      updateAccountUI();
      updateCartUI();
      toggleAccountModal(false);
      showToast(`Tebrikler ${fullName}! MivoCat hesabınız oluşturuldu ve giriş yapıldı.`, "Hesap Oluşturuldu", "✨", false);
    } catch (err) {
      showToast("Shopify kayıt sunucusuna bağlanırken bir hata oluştu.", "Bağlantı Hatası", "⚠️", true);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "✨ MİVOCAT HESABI OLUŞTUR";
      }
    }
  });

  // Handle local preview vs Shopify live navigation for policy links
  document.querySelectorAll('.footer-policy-link, .footer-nav-links a[data-page-handle]').forEach(link => {
    link.addEventListener('click', (e) => {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal) {
        e.preventDefault();
        const pageTitle = link.textContent.trim();
        const handle = link.getAttribute('data-page-handle');
        showToast(`📄 '${pageTitle}' sayfası canlı mağazanızda (/pages/${handle}) açılır.`, "Shopify Sayfa Bağlantısı", "ℹ️");
      }
    });
  });

  // Forgot Form Submission (Official Shopify Customer Recover GraphQL API)
  forgotForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = document.getElementById('forgot-email')?.value.trim();
    if (!email) return;

    const res = await recoverShopifyPassword(email);
    toggleAccountModal(false);

    if (res.success) {
      showToast(`${email} adresinize şifre sıfırlama e-postası gönderildi. Lütfen gelen kutunuzu kontrol edin.`, "E-posta Gönderildi", "🔑");
    } else {
      showToast(res.error || 'Şifre sıfırlama e-postası gönderilemedi. Adresinizi kontrol edin.', "Şifre Uyarısı", "⚠️");
    }
  });

  // Upsell Add-on Action (Tüy Temizleme Rulosu 99 TL)
  document.getElementById('add-upsell-lint')?.addEventListener('click', () => {
    state.cart.push({
      id: "gid://shopify/Product/999111222",
      title: "Pratik Tüy Temizleme Rulosu",
      price: 99.00,
      color: "Standart",
      size: "Tek Ebat",
      image: "assets/images/krem-real.png",
      quantity: 1
    });
    updateCartUI();
    showToast("Pratik Tüy Temizleme Rulosu (99,00 TL) sepetinize eklendi!", "Sepete Eklendi", "✨");
  });

  // Coupon Application
  document.getElementById('apply-coupon-btn')?.addEventListener('click', () => {
    const input = document.getElementById('coupon-input');
    const code = input?.value.trim().toUpperCase();
    if (code === "MIVO10" || code === "PATI10") {
      state.discountAmount = 50.00; // 50 TL discount
      updateCartUI();
      showToast("50,00 TL İndirim Kuponu Uygulandı!", "Kupon Kabul Edildi", "🎉");
    } else if (code) {
      showToast("Geçersiz kupon kodu. Örnek geçerli kupon: MIVO10", "Geçersiz Kupon", "⚠️");
    }
  });

  // Cart Items Container Event Delegation (Remove & Qty Controls)
  const cartContainer = document.getElementById('cart-items-container');
  if (cartContainer) {
    cartContainer.addEventListener('click', (e) => {
      // 1. Remove item button
      const removeBtn = e.target.closest('.cart-item-remove-btn');
      if (removeBtn) {
        const idx = parseInt(removeBtn.getAttribute('data-index'), 10);
        if (!isNaN(idx) && state.cart[idx]) {
          state.cart.splice(idx, 1);
          updateCartUI();
        }
        return;
      }

      // 2. Quantity Minus button
      const minusBtn = e.target.closest('.cart-qty-minus');
      if (minusBtn) {
        const idx = parseInt(minusBtn.getAttribute('data-index'), 10);
        if (!isNaN(idx) && state.cart[idx]) {
          if (state.cart[idx].quantity > 1) {
            state.cart[idx].quantity--;
          } else {
            state.cart.splice(idx, 1);
          }
          updateCartUI();
        }
        return;
      }

      // 3. Quantity Plus button
      const plusBtn = e.target.closest('.cart-qty-plus');
      if (plusBtn) {
        const idx = parseInt(plusBtn.getAttribute('data-index'), 10);
        if (!isNaN(idx) && state.cart[idx]) {
          state.cart[idx].quantity++;
          updateCartUI();
        }
        return;
      }
    });
  }
}

// Cart Logic
function addToCart() {
  const colorName = typeof state.selectedColor === 'object' ? state.selectedColor.name : state.selectedColor;
  const colorImage = typeof state.selectedColor === 'object' ? state.selectedColor.image : state.product.media[0].src;
  const variantId = getSelectedVariantId();

  const existingIndex = state.cart.findIndex(
    item => item.color === colorName && item.size === state.selectedSize
  );

  if (existingIndex > -1) {
    state.cart[existingIndex].quantity += state.quantity;
    state.cart[existingIndex].id = variantId; // Ensure variant ID is correct
  } else {
    state.cart.push({
      id: variantId,
      title: state.product.title,
      price: parseFloat(state.product.price.amount),
      color: colorName,
      size: state.selectedSize,
      image: colorImage || state.product.media[0].src,
      quantity: state.quantity
    });
  }

  updateCartUI();
}

function updateCartUI() {
  // Save cart state persistently to current user's personal storage key
  try {
    const key = getCartStorageKey(state.user);
    localStorage.setItem(key, JSON.stringify(state.cart));
  } catch (err) {}

  const totalCount = state.cart.reduce((sum, item) => sum + item.quantity, 0);
  let subtotal = state.cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  if (state.discountAmount) {
    subtotal = Math.max(0, subtotal - state.discountAmount);
  }

  document.getElementById('cart-count-badge').textContent = totalCount;
  document.getElementById('cart-drawer-count').textContent = totalCount;
  document.getElementById('cart-subtotal-price').textContent = `${subtotal.toFixed(2)} TL`;

  // Free Shipping Progress calculation (500 TL threshold)
  const shippingThreshold = 500;
  const progressFill = document.getElementById('shipping-progress-fill');
  const progressText = document.getElementById('shipping-progress-text');

  if (progressFill && progressText) {
    if (subtotal >= shippingThreshold) {
      progressFill.style.width = '100%';
      progressText.innerHTML = `🎉 <strong>TEBRİKLER!</strong> Siparişiniz <strong>ÜCRETSİZ KARGO</strong> Kapsamında!`;
    } else {
      const remaining = shippingThreshold - subtotal;
      const percentage = Math.min(100, (subtotal / shippingThreshold) * 100);
      progressFill.style.width = `${percentage}%`;
      progressText.innerHTML = `🚚 Ücretsiz kargoya son <strong>${remaining.toFixed(2)} TL</strong> kaldı!`;
    }
  }

  const container = document.getElementById('cart-items-container');
  if (!container) return;

  if (state.cart.length === 0) {
    container.innerHTML = `
      <div class="empty-cart-view" id="empty-cart-msg">
        <span class="empty-icon">🐾</span>
        <p class="empty-title">Sepetiniz Şimdilik Boş</p>
        <small class="empty-sub">Kediniz Mivo Patiyuva™ ile kalbinizin üstünde taşınmaya hazır!</small>
      </div>
    `;
  } else {
    container.innerHTML = state.cart.map((item, index) => `
      <div class="cart-item-card" data-index="${index}">
        <img src="${item.image}" alt="${item.title}" class="cart-item-img">
        <div class="cart-item-details">
          <div class="cart-item-top">
            <span class="cart-item-title">${item.title}</span>
            <button type="button" class="cart-item-remove-btn" data-index="${index}" title="Ürünü Sil">&times;</button>
          </div>
          <span class="cart-item-variant">Renk: ${item.color} | Beden: ${item.size}</span>
          <div class="cart-item-bottom">
            <div class="quantity-selector qty-sm">
              <button type="button" class="qty-btn cart-qty-minus" data-index="${index}">-</button>
              <input type="number" value="${item.quantity}" readonly>
              <button type="button" class="qty-btn cart-qty-plus" data-index="${index}">+</button>
            </div>
            <span class="cart-item-price">${(item.price * item.quantity).toFixed(2)} TL</span>
          </div>
        </div>
      </div>
    `).join('');
  }
}

// 8. Live Viewers Dynamic Counter
function initLiveCounters() {
  const viewerText = document.getElementById('live-viewer-text');
  if (!viewerText) return;
  setInterval(() => {
    const randomViewers = Math.floor(Math.random() * 12) + 38; // Random 38 - 50
    viewerText.innerHTML = `Şu an <strong>${randomViewers} kedisever</strong> bu ürünü inceliyor.`;
  }, 12000);
}

/**
 * SHOPIFY STOREFRONT API INTEGRATION HELPER
 * 
 * Future Usage: Connect directly to your Shopify store GraphQL API:
 * 
 * async function fetchShopifyProduct(handle) {
 *   const response = await fetch('https://YOUR_STORE.myshopify.com/api/2024-04/graphql.json', {
 *     method: 'POST',
 *     headers: {
 *       'Content-Type': 'application/json',
 *       'X-Shopify-Storefront-Access-Token': 'YOUR_STOREFRONT_ACCESS_TOKEN'
 *     },
 *     body: JSON.stringify({
 *       query: `{ productByHandle(handle: "${handle}") { id title description priceRange { minVariantPrice { amount currencyCode } } } }`
 *     })
 *   });
 *   return await response.json();
 * }
 */
